/**
 * useLocalStorageState — like useState, but the value is mirrored into
 * localStorage so it survives a browser refresh.
 *
 * Designed for view-state preferences that the user expects to "stick"
 * across reloads: filter selections, look-ahead windows, search terms,
 * pane widths. NOT for sensitive data, large blobs, or anything that
 * should be scoped per-tab (use sessionStorage for those).
 *
 * Behaviour:
 *   - On mount, read the stored value (lazy initializer — avoids re-reads).
 *   - On every change, write the value back. JSON-serialised.
 *   - Corrupt JSON in storage → silently fall back to `defaultValue`.
 *   - Storage write failures (quota, private mode) → silently swallowed.
 *
 * Drop-in: `const [foo, setFoo] = useLocalStorageState('epicplan.foo', 'bar');`
 */
import { useState, useEffect } from 'react';

export function useLocalStorageState(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch {
      // Corrupt JSON or storage unavailable — fall back to default.
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded / private mode / storage disabled — drop silently.
      // The in-memory value still works for the current session.
    }
  }, [key, value]);

  return [value, setValue];
}

export default useLocalStorageState;
